import React, { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { useQueryClient } from '@tanstack/react-query';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showToast } from '@/components/Layout/Toast';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useBrokerConnections } from '@/hooks/useBrokerConnections';
import { Button, ConfirmationDialog, DataTable, FormInput, FormSelect, Modal } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import type { Brokerage } from '@einhaus/project314-types';
import { useAuth } from '@/context/AuthContext';
import SupportRestrictedFeatureCard from '@/components/Support/SupportRestrictedFeatureCard';

interface BrokerConnectionRow {
    id: number;
    brokerage: Brokerage;
    name: string;
    status: 'active' | 'expired' | 'revoked' | 'error' | 'reauthorizationRequired' | 'invalid' | 'disconnected';
    authMethod: 'oauth' | 'apiKeys';
    ownershipMode: 'platformApp' | 'userApp' | 'userKeys';
    environmentScope: 'live' | 'paper' | 'both';
    updatedDateTime: string;
    linkedTradingAccountsCount: number;
    attentionSeverity: 'warning' | 'error' | null;
    attentionMessage: string | null;
    reconnectDeadlineDateTime: string | null;
}

type ConnectionMode = 'platformOauth' | 'userOauthApp' | 'userApiKeys';

const SUPPORTED_BROKERAGES: Brokerage[] = ['TradeStation', 'Schwab', 'Alpaca'];

function getConnectionModeOptions(brokerage: Brokerage) {
    const options = [
        { value: 'platformOauth', label: 'Platform OAuth' },
        { value: 'userOauthApp', label: 'My OAuth App' }
    ];

    if (brokerage === 'Alpaca') {
        options.push({ value: 'userApiKeys', label: 'My API Keys' });
    }

    return options;
}

function getDefaultConnectionMode(brokerage: Brokerage): ConnectionMode {
    return brokerage === 'Alpaca' ? 'platformOauth' : 'userOauthApp';
}

function getOauthProviderRouteId(brokerage: Brokerage) {
    switch (brokerage) {
        case 'TradeStation':
            return 'tradestation';
        case 'Schwab':
            return 'schwab';
        case 'Alpaca':
            return 'alpaca';
        default:
            return 'tradestation';
    }
}

function getOauthRedirectUrl(brokerage: Brokerage) {
    return `${window.location.origin}/oauthCallback/${getOauthProviderRouteId(brokerage)}`;
}

function formatConnectionMethod(connection: BrokerConnectionRow) {
    if (connection.authMethod === 'apiKeys') {
        return 'User API Keys';
    }

    return connection.ownershipMode === 'userApp' ? 'User OAuth App' : 'Platform OAuth';
}

function formatEnvironmentScope(environmentScope: BrokerConnectionRow['environmentScope']) {
    if (environmentScope === 'both') return 'Live + Paper';
    return environmentScope === 'paper' ? 'Paper' : 'Live';
}

function getStatusLabel(status: BrokerConnectionRow['status']) {
    if (status === 'reauthorizationRequired') {
        return 'Reauthorization Required';
    }

    return status.charAt(0).toUpperCase() + status.slice(1);
}

const ThirdPartyConnections: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingConnection, setEditingConnection] = useState<BrokerConnectionRow | null>(null);
    const [selectedBrokerage, setSelectedBrokerage] = useState<Brokerage>('TradeStation');
    const [connectionMode, setConnectionMode] = useState<ConnectionMode>(getDefaultConnectionMode('TradeStation'));
    const [connectionName, setConnectionName] = useState('');
    const [environmentScope, setEnvironmentScope] = useState<'live' | 'paper' | 'both'>('both');
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [pendingOauthRequest, setPendingOauthRequest] = useState<{ service: Brokerage; brokerConnectionId: number } | null>(null);
    const [pendingDisconnectConnection, setPendingDisconnectConnection] = useState<BrokerConnectionRow | null>(null);
    const [pendingDeleteConnection, setPendingDeleteConnection] = useState<BrokerConnectionRow | null>(null);

    const { sessionPolicy } = useAuth();
    const queryClient = useQueryClient();
    const { canManageBrokerConnections } = sessionPolicy;

    const { brokerConnections: fetchedBrokerConnections, isLoading: isBrokerConnectionsLoading } = useBrokerConnections({
        enabled: canManageBrokerConnections,
        errorConfig: {
            fallbackErrorMessage: 'Failed to load broker connections',
            showErrorToast: true,
            setError: (message) => setError(message)
        }
    });

    const oauthUrlQuery = useAppQuery({
        ...trpc.user.fetchOauthUrl.queryOptions({
            service: pendingOauthRequest?.service ?? 'TradeStation',
            brokerConnectionId: pendingOauthRequest?.brokerConnectionId ?? null
        }),
        enabled: canManageBrokerConnections && pendingOauthRequest !== null
    });

    useEffect(() => {
        if (oauthUrlQuery.error) {
            handleTrpcQuery(oauthUrlQuery, {
                fallbackErrorMessage: 'Failed to get authorization URL',
                showErrorToast: true,
                setError: (message) => setError(message)
            });

            const timeoutId = window.setTimeout(() => {
                setPendingOauthRequest(null);
            }, 0);

            return () => window.clearTimeout(timeoutId);
        }
    }, [oauthUrlQuery.error]);

    useEffect(() => {
        if (oauthUrlQuery.data?.url) {
            window.location.href = oauthUrlQuery.data.url;
        }
    }, [oauthUrlQuery.data]);

    const saveBrokerConnectionMutation = useTrpcMutation(
        trpc.user.saveBrokerConnection.mutationOptions({
            onSuccess: async (data, variables) => {
                await queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchBrokerConnections.queryOptions().queryKey
                });

                if (!data.success) return;

                if (data.requiresOauthAuthorization) {
                    setIsCreateModalOpen(false);
                    setEditingConnection(null);

                    setPendingOauthRequest({
                        service: variables.brokerage,
                        brokerConnectionId: data.brokerConnectionId
                    });

                    return;
                }

                showToast('Broker connection saved', 'success');
                handleCloseCreateModal();
            }
        }),
        {
            fallbackErrorMessage: 'Failed to save broker connection'
        }
    );

    const disconnectBrokerConnectionMutation = useTrpcMutation(
        trpc.user.disconnectBrokerConnection.mutationOptions({
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchBrokerConnections.queryOptions().queryKey
                });
            }
        }),
        {
            successMessage: 'Broker connection updated',
            fallbackErrorMessage: 'Failed to disconnect broker connection'
        }
    );

    const deleteBrokerConnectionMutation = useTrpcMutation(
        trpc.user.deleteBrokerConnection.mutationOptions({
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchBrokerConnections.queryOptions().queryKey
                });
            }
        }),
        {
            successMessage: 'Broker connection deleted',
            fallbackErrorMessage: 'Failed to delete broker connection'
        }
    );

    const brokerConnections = fetchedBrokerConnections as BrokerConnectionRow[];

    const connectionModeOptions = useMemo(() => getConnectionModeOptions(selectedBrokerage), [selectedBrokerage]);

    const shouldCollectUserCredentials = connectionMode === 'userOauthApp' || connectionMode === 'userApiKeys';
    const isOauthConnection = connectionMode !== 'userApiKeys';

    const connectionRows = useMemo(
        () =>
            brokerConnections.map((connection) => ({
                ...connection,
                methodLabel: formatConnectionMethod(connection),
                environmentLabel: formatEnvironmentScope(connection.environmentScope),
                statusLabel: getStatusLabel(connection.status)
            })),
        [brokerConnections]
    );

    const connectionAttentionItems = useMemo(
        () => connectionRows.filter((connection) => Boolean(connection.attentionMessage)),
        [connectionRows]
    );

    const columns: ColumnDef<(typeof connectionRows)[number]>[] = [
        {
            header: 'Brokerage',
            accessorKey: 'brokerage'
        },
        {
            header: 'Name',
            accessorKey: 'name'
        },
        {
            header: 'Auth',
            accessorKey: 'methodLabel'
        },
        {
            header: 'Scope',
            accessorKey: 'environmentLabel'
        },
        {
            header: 'Status',
            accessorKey: 'statusLabel'
        },
        {
            header: 'Updated',
            accessorKey: 'updatedDateTime'
        },
        {
            header: 'Linked Accounts',
            accessorKey: 'linkedTradingAccountsCount'
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: ({ row }) => {
                const connection = row.original;

                return (
                    <div className="flex gap-2">
                        {connection.authMethod === 'oauth' && (
                            <Button
                                size="sm"
                                variant="primary"
                                onClick={() =>
                                    setPendingOauthRequest({
                                        service: connection.brokerage,
                                        brokerConnectionId: connection.id
                                    })
                                }
                            >
                                {connection.status === 'active' ? 'Re-authorize' : 'Authorize'}
                            </Button>
                        )}
                        <Button size="sm" variant="secondary" onClick={() => handleEditConnection(connection)}>
                            Edit
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => handleDisconnectConnection(connection)}>
                            {connection.authMethod === 'apiKeys' ? 'Deactivate' : 'Disconnect'}
                        </Button>
                        <Button
                            disabled={connection.linkedTradingAccountsCount > 0}
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeleteConnection(connection)}
                        >
                            Delete
                        </Button>
                    </div>
                );
            }
        }
    ];

    if (!canManageBrokerConnections) {
        return (
            <UserLayout title="Third Party Connections - Project 314">
                <SupportRestrictedFeatureCard
                    description="Third-party brokerage connections are disabled during a read-only support session because they can create, refresh, or revoke external authorizations."
                    title="Third-Party Connections Are Unavailable"
                />
            </UserLayout>
        );
    }

    const handleOpenCreateModal = () => {
        setEditingConnection(null);
        setSelectedBrokerage('TradeStation');
        setConnectionMode(getDefaultConnectionMode('TradeStation'));
        setConnectionName('');
        setEnvironmentScope('both');
        setClientId('');
        setClientSecret('');
        setIsCreateModalOpen(true);
    };

    const handleEditConnection = (connection: BrokerConnectionRow) => {
        setEditingConnection(connection);
        setSelectedBrokerage(connection.brokerage);

        setConnectionMode(
            connection.authMethod === 'apiKeys' ? 'userApiKeys' : connection.ownershipMode === 'userApp' ? 'userOauthApp' : 'platformOauth'
        );

        setConnectionName(connection.name);
        setEnvironmentScope(connection.environmentScope);
        setClientId('');
        setClientSecret('');
        setIsCreateModalOpen(true);
    };

    const handleDisconnectConnection = (connection: BrokerConnectionRow) => {
        setPendingDisconnectConnection(connection);
    };

    const handleDeleteConnection = (connection: BrokerConnectionRow) => {
        if (connection.linkedTradingAccountsCount > 0) {
            showToast('Remove or move linked trading accounts before deleting this connection', 'error');
            return;
        }

        setPendingDeleteConnection(connection);
    };

    const confirmDisconnectConnection = () => {
        if (!pendingDisconnectConnection) return;
        disconnectBrokerConnectionMutation.mutate({ id: pendingDisconnectConnection.id });
        setPendingDisconnectConnection(null);
    };

    const confirmDeleteConnection = () => {
        if (!pendingDeleteConnection) return;
        deleteBrokerConnectionMutation.mutate({ id: pendingDeleteConnection.id });
        setPendingDeleteConnection(null);
    };

    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
        setEditingConnection(null);
        setConnectionName('');
        setClientId('');
        setClientSecret('');
        setEnvironmentScope('both');
        setSelectedBrokerage('TradeStation');
        setConnectionMode(getDefaultConnectionMode('TradeStation'));
    };

    const handleBrokerageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (editingConnection) return;

        const nextBrokerage = event.target.value as Brokerage;
        const nextConnectionMode = getDefaultConnectionMode(nextBrokerage);

        setSelectedBrokerage(nextBrokerage);
        setConnectionMode(nextConnectionMode);
        setEnvironmentScope(nextConnectionMode === 'userApiKeys' ? 'paper' : 'both');
        setClientId('');
        setClientSecret('');
    };

    const handleConnectionModeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextMode = event.target.value as ConnectionMode;
        setConnectionMode(nextMode);
        setEnvironmentScope(nextMode === 'userApiKeys' ? 'paper' : 'both');
        setClientId('');
        setClientSecret('');
    };

    const handleCreateConnection = () => {
        const authMethod = connectionMode === 'userApiKeys' ? 'apiKeys' : 'oauth';
        const normalizedClientId = clientId.trim();
        const normalizedClientSecret = clientSecret.trim();

        const ownershipMode =
            connectionMode === 'platformOauth' ? 'platformApp' : connectionMode === 'userOauthApp' ? 'userApp' : 'userKeys';

        saveBrokerConnectionMutation.mutate({
            id: editingConnection?.id ?? null,
            brokerage: selectedBrokerage,
            name: connectionName.trim() || null,
            authMethod,
            ownershipMode,
            environmentScope: authMethod === 'apiKeys' ? environmentScope : 'both',
            clientId: shouldCollectUserCredentials && normalizedClientId ? normalizedClientId : undefined,
            clientSecret: shouldCollectUserCredentials && normalizedClientSecret ? normalizedClientSecret : undefined
        });
    };

    const credentialLabels =
        connectionMode === 'userApiKeys'
            ? {
                  clientId: 'API Key ID',
                  clientSecret: 'API Secret Key'
              }
            : {
                  clientId: 'Client ID',
                  clientSecret: 'Client Secret'
              };

    return (
        <UserLayout title="Third-Party Connections - Project 314">
            <div className="container mx-auto px-4">
                <div className="flex justify-between items-center mt-4 mb-4">
                    <div>
                        <h3 className="text-white text-xl font-semibold">Broker Connections</h3>
                    </div>
                    <Button variant="primary" onClick={handleOpenCreateModal}>
                        Add Connection
                    </Button>
                </div>

                {error && (
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md" role="alert">
                        {error}
                    </div>
                )}

                {connectionAttentionItems.length > 0 && (
                    <div className="space-y-3 mb-4">
                        {connectionAttentionItems.map((connection) => (
                            <div
                                className={`rounded-md border p-4 ${
                                    connection.attentionSeverity === 'error'
                                        ? 'border-red-500/60 bg-red-950/40 text-red-100'
                                        : 'border-amber-500/60 bg-amber-950/40 text-amber-100'
                                }`}
                                key={connection.id}
                                role="alert"
                            >
                                <div className="font-semibold">
                                    {connection.name} ({connection.brokerage})
                                </div>
                                <div className="mt-1 text-sm">{connection.attentionMessage}</div>
                            </div>
                        ))}
                    </div>
                )}

                {isBrokerConnectionsLoading ? (
                    <DataLoadingSpinner text="Loading broker connections..." />
                ) : (
                    <DataTable search sort striped columns={columns} data={connectionRows} pagination={{ enabled: true, pageSize: 20 }} />
                )}

                <Modal
                    footer={
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={handleCloseCreateModal}>
                                Cancel
                            </Button>
                            <Button
                                disabled={
                                    saveBrokerConnectionMutation.isPending ||
                                    oauthUrlQuery.isFetching ||
                                    disconnectBrokerConnectionMutation.isPending ||
                                    deleteBrokerConnectionMutation.isPending
                                }
                                variant="primary"
                                onClick={handleCreateConnection}
                            >
                                {isOauthConnection
                                    ? editingConnection
                                        ? 'Save Connection'
                                        : 'Save and Continue'
                                    : editingConnection
                                      ? 'Save Changes'
                                      : 'Save Connection'}
                            </Button>
                        </div>
                    }
                    isOpen={isCreateModalOpen}
                    size="lg"
                    title={editingConnection ? 'Edit Broker Connection' : 'Add Broker Connection'}
                    onClose={handleCloseCreateModal}
                >
                    <div className="space-y-4">
                        <FormSelect
                            disabled={editingConnection !== null}
                            id="brokerage"
                            label="Brokerage"
                            options={SUPPORTED_BROKERAGES.map((brokerage) => ({
                                value: brokerage,
                                label: brokerage
                            }))}
                            value={selectedBrokerage}
                            onChange={handleBrokerageChange}
                        />

                        <FormSelect
                            id="connectionMode"
                            label="Connection Type"
                            options={connectionModeOptions}
                            value={connectionMode}
                            onChange={handleConnectionModeChange}
                        />

                        <FormInput
                            id="connectionName"
                            label="Connection Name"
                            placeholder={`${selectedBrokerage} Connection`}
                            value={connectionName}
                            onChange={(event) => setConnectionName(event.target.value)}
                        />

                        {connectionMode === 'userApiKeys' && (
                            <FormSelect
                                id="environmentScope"
                                label="Environment"
                                options={[
                                    { value: 'paper', label: 'Paper' },
                                    { value: 'live', label: 'Live' }
                                ]}
                                value={environmentScope}
                                onChange={(event) => setEnvironmentScope(event.target.value as 'live' | 'paper')}
                            />
                        )}

                        {shouldCollectUserCredentials && (
                            <>
                                <FormInput
                                    helpText={editingConnection ? 'Leave blank to keep the current stored value.' : undefined}
                                    id="clientId"
                                    label={credentialLabels.clientId}
                                    value={clientId}
                                    onChange={(event) => setClientId(event.target.value)}
                                />
                                <FormInput
                                    helpText={editingConnection ? 'Leave blank to keep the current stored value.' : undefined}
                                    id="clientSecret"
                                    label={credentialLabels.clientSecret}
                                    type="password"
                                    value={clientSecret}
                                    onChange={(event) => setClientSecret(event.target.value)}
                                />
                            </>
                        )}

                        {isOauthConnection && (
                            <div className="bg-zinc-900 border border-zinc-700 rounded-md p-3 text-sm text-zinc-300">
                                Redirect URI: <span className="font-mono break-all">{getOauthRedirectUrl(selectedBrokerage)}</span>
                            </div>
                        )}
                    </div>
                </Modal>

                <ConfirmationDialog
                    isConfirmLoading={disconnectBrokerConnectionMutation.isPending}
                    isOpen={pendingDisconnectConnection !== null}
                    message={
                        pendingDisconnectConnection
                            ? `Disconnect ${pendingDisconnectConnection.name}?`
                            : 'Disconnect this broker connection?'
                    }
                    title="Disconnect Broker Connection"
                    onCancel={() => setPendingDisconnectConnection(null)}
                    onConfirm={confirmDisconnectConnection}
                />

                <ConfirmationDialog
                    isConfirmLoading={deleteBrokerConnectionMutation.isPending}
                    isOpen={pendingDeleteConnection !== null}
                    message={
                        pendingDeleteConnection
                            ? `Delete ${pendingDeleteConnection.name}? This cannot be undone.`
                            : 'Delete this broker connection?'
                    }
                    title="Delete Broker Connection"
                    onCancel={() => setPendingDeleteConnection(null)}
                    onConfirm={confirmDeleteConnection}
                />
            </div>
        </UserLayout>
    );
};

export default ThirdPartyConnections;
